import { parseJsonSafe } from "@sinch/utils/json";
import { isNilOrEmpty } from "ramda-adjunct";
import { useCallback, useEffect, useState } from "react";
import { FieldValues } from "react-hook-form";

const EXPIRATION_PREFIX = "__expiration__";

export interface UseLocalStorage<TFieldValues extends FieldValues> {
  /**
   * Value to be saved to localstorage
   */
  localStorage: TFieldValues;
  /**
   * Function to get saved value from localstorage
   */
  setLocalStorage: (value: TFieldValues) => void;
}

/**
 * Custom hook to manage local storage with optional expiration.
 *
 * @template TFieldValues - The type of the stored value.
 * @param {string} localStorageKey - The key to store the value under in local storage.
 * @param {Object} [options] - Optional settings.
 * @param {TFieldValues} [options.defaultValue] - The default value to use if none is found in local storage.
 * @param {Date} [options.expiration] - The expiration date for the stored value.
 * @returns {[TFieldValues | undefined, (value: TFieldValues) => void, () => void]} - The stored value, a function to update it, and a function to remove it.
 */
export const useLocalStorage = <TFieldValues = any,>(
  localStorageKey: string,
  { defaultValue, expiration }: { defaultValue?: TFieldValues; expiration?: Date } = {}
): [TFieldValues | undefined, (value: TFieldValues) => void, () => void] => {
  const initStoreValue = getLocalStore(localStorageKey);
  const initDefaultValue = !isNilOrEmpty(initStoreValue) ? initStoreValue : defaultValue;

  const [localStorage, setLocalStorage] = useState<TFieldValues | undefined>(initDefaultValue);

  /**
   * Handle changes to the local storage.
   */
  const handleStoreChange = useCallback(() => {
    const storageData = globalThis.localStorage.getItem(localStorageKey);
    if (storageData !== JSON.stringify(localStorage)) {
      setLocalStorage(parseJsonSafe(storageData));
    }
  }, [localStorage, localStorageKey]);

  useEffect(() => {
    if (isNilOrEmpty(localStorage)) {
      removeLocalStorage(localStorageKey)();
    } else {
      setLocalStore(localStorageKey, localStorage, expiration);
    }
  }, [localStorage, localStorageKey]);

  useEffect(() => {
    globalThis.addEventListener("storage", handleStoreChange);
    return () => globalThis.removeEventListener("storage", handleStoreChange);
  }, [handleStoreChange]);

  // Clean up all expired entries, not just the current key
  deleteAllExpiredEntries();

  const handleSetLocalStorage = (value: TFieldValues | undefined) => {
    if (isNilOrEmpty(value)) {
      removeLocalStorage(localStorageKey)();
    } else {
      setLocalStore(localStorageKey, value, expiration);
      handleStoreChange();
    }
  };

  return [localStorage, handleSetLocalStorage, removeLocalStorage(localStorageKey)];
};

/**
 * Set a value in local storage with optional expiration.
 * If quota is exceeded, tries to free space by deleting oldest expirable entries.
 *
 * @param {string} localStorageKey - The key to store the value under in local storage.
 * @param {any} state - The value to store.
 * @param {Date} [expiration] - The expiration date for the stored value.
 */
export const setLocalStore = (localStorageKey: string, state: any, expiration?: Date) => {
  const maxRetries = countExpirationEntries();

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      globalThis.localStorage.setItem(localStorageKey, JSON.stringify(state));
      if (expiration) {
        globalThis.localStorage.setItem(`${EXPIRATION_PREFIX}${localStorageKey}`, expiration.toISOString());
      }
      return; // Success
    } catch (e) {
      if (isQuotaExceededError(e)) {
        // Try to free space by deleting oldest expirable entry
        const deleted = deleteOldestExpirableEntry();
        if (!deleted) {
          // No expirable entries left to delete, cannot recover
          console.error("LocalStorage quota exceeded and no expirable entries to delete", e);
          return;
        }
        // Continue to next retry attempt
      } else {
        // Non-quota error, log and exit
        console.error(e);
        return;
      }
    }
  }

  // Max retries reached
  console.error("LocalStorage quota exceeded after maximum retry attempts");
};

/**
 * Remove a value from local storage.
 *
 * @param {string} localStorageKey - The key of the value to remove from local storage.
 * @returns {() => void} - A function to remove the value.
 */
export const removeLocalStorage = (localStorageKey: string) => () => {
  globalThis.localStorage.removeItem(localStorageKey);
  globalThis.localStorage.removeItem(`${EXPIRATION_PREFIX}${localStorageKey}`);
};

/**
 * Get a value from local storage.
 *
 * @param {string} localStorageKey - The key of the value to retrieve from local storage.
 * @returns {any} - The retrieved value, or null if not found.
 */
export const getLocalStore = (localStorageKey: string) => {
  const data = globalThis.localStorage.getItem(localStorageKey);
  if (data === null) {
    return null;
  }
  return parseJsonSafe(data);
};

/**
 * Check if a value in local storage has expired.
 *
 * @param {string} localStorageKey - The key of the value to check.
 * @returns {boolean} - True if the value has expired, false otherwise.
 */
export const isExpired = (localStorageKey: string) => {
  const data = globalThis.localStorage.getItem(`${EXPIRATION_PREFIX}${localStorageKey}`);
  if (data === null) {
    return false;
  }
  return new Date(data) < new Date();
};

/**
 * Check if an error is a QuotaExceededError.
 */
const isQuotaExceededError = (error: unknown): boolean =>
  error instanceof DOMException &&
  // Firefox uses NS_ERROR_DOM_QUOTA_REACHED
  (error.name === "QuotaExceededError" || error.name === "NS_ERROR_DOM_QUOTA_REACHED" || error.code === 22);

/**
 * Count the number of expiration entries in localStorage.
 */
const countExpirationEntries = (): number => {
  let count = 0;
  for (let i = 0; i < globalThis.localStorage.length; i++) {
    const key = globalThis.localStorage.key(i);
    if (key?.startsWith(EXPIRATION_PREFIX)) {
      count++;
    }
  }
  return count;
};

/**
 * Delete all expired entries from localStorage.
 * Iterates through all keys with expiration dates and removes those that have expired.
 */
const deleteAllExpiredEntries = (): void => {
  const locStore = globalThis.localStorage;
  const keysToDelete: string[] = [];

  // Collect all expired keys first (avoid modifying during iteration)
  for (let i = 0; i < locStore.length; i++) {
    const key = locStore.key(i);
    if (key?.startsWith(EXPIRATION_PREFIX)) {
      const dateStr = locStore.getItem(key);
      if (dateStr && new Date(dateStr) < new Date()) {
        keysToDelete.push(key);
      }
    }
  }

  // Delete all expired entries
  for (const expirationKey of keysToDelete) {
    const dataKey = expirationKey.substring(EXPIRATION_PREFIX.length);
    locStore.removeItem(dataKey);
    locStore.removeItem(expirationKey);
  }
};

/**
 * Find and delete the oldest expirable entry from localStorage.
 * @returns true if an entry was deleted, false if no expirable entries exist.
 */
const deleteOldestExpirableEntry = (): boolean => {
  let oldestKey: string | null = null;
  let oldestDate: Date | null = null;
  const locStore = globalThis.localStorage;

  // Find all expiration entries and track the oldest
  for (let i = 0; i < locStore.length; i++) {
    const key = locStore.key(i);
    if (key?.startsWith(EXPIRATION_PREFIX)) {
      const dateStr = locStore.getItem(key);
      if (dateStr) {
        const date = new Date(dateStr);
        if (!oldestDate || date < oldestDate) {
          oldestDate = date;
          oldestKey = key;
        }
      }
    }
  }

  if (oldestKey) {
    // Extract the actual storage key (remove __expiration__ prefix)
    const dataKey = oldestKey.substring(EXPIRATION_PREFIX.length);
    locStore.removeItem(dataKey);
    locStore.removeItem(oldestKey);
    return true;
  }

  return false;
};
